// components/LocationTypeManager.tsx
import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, AlertTriangle } from 'lucide-react';
import { LocationType } from '../types/locationTypes';
import { locationTypesApi } from '../services/locationTypesApi';

interface LocationTypeManagerProps {
    onClose: () => void;
}

const LocationTypeManager: React.FC<LocationTypeManagerProps> = ({ onClose }) => {
    const [types, setTypes] = useState<LocationType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingType, setEditingType] = useState<LocationType | null>(null);
    const [newTypeName, setNewTypeName] = useState('');
    const [newTypeDescription, setNewTypeDescription] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchLocationTypes();
    }, []);

    const fetchLocationTypes = async () => {
        try {
            setLoading(true);
            const data = await locationTypesApi.getAllLocationTypes();
            setTypes(data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch location types');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTypeName.trim() || !newTypeDescription.trim()) {
            setError('Please fill in all required fields');
            return;
        }

        try {
            setIsSubmitting(true);
            setError(null);
            
            await locationTypesApi.createLocationType({
                typeName: newTypeName.trim(),
                description: newTypeDescription.trim(),
                active: true
            });
            
            await fetchLocationTypes();
            setNewTypeName('');
            setNewTypeDescription('');
        } catch (err) {
            setError('Failed to create location type');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            setIsSubmitting(true);
            await locationTypesApi.deleteLocationType(id);
            await fetchLocationTypes();
            setShowDeleteConfirm(null);
        } catch (err) {
            setError('Failed to delete location type');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdate = async (type: LocationType) => {
        if (!type.typeName.trim() || !type.description.trim()) {
            setError('Please fill in all required fields');
            return;
        }

        try {
            setIsSubmitting(true);
            await locationTypesApi.updateLocationType(type.locationTypeId!, {
                typeName: type.typeName.trim(),
                description: type.description.trim(),
                active: type.active
            });
            await fetchLocationTypes();
            setEditingType(null);
        } catch (err) {
            setError('Failed to update location type');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-lg font-semibold">Manage Location Types</h2>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                    <X className="w-5 h-5" />
                </button>
            </div>

            {error && (
                <div className="p-4 bg-red-50 text-red-700">
                    <div className="flex items-center">
                        <AlertTriangle className="w-5 h-5 mr-2" />
                        {error}
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="p-4 border-b">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Type Name</label>
                        <input
                            type="text"
                            value={newTypeName}
                            onChange={(e) => setNewTypeName(e.target.value)}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            required
                            disabled={isSubmitting}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <input
                            type="text"
                            value={newTypeDescription}
                            onChange={(e) => setNewTypeDescription(e.target.value)}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            required
                            disabled={isSubmitting}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        {isSubmitting ? 'Adding...' : 'Add Location Type'}
                    </button>
                </div>
            </form>

            <div className="flex-1 overflow-y-auto p-4">
                {loading ? (
                    <div className="text-center py-4">Loading...</div>
                ) : (
                    <div className="space-y-2">
                        {types.map((type) => (
                            <div
                                key={type.locationTypeId}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                            >
                                {editingType?.locationTypeId === type.locationTypeId ? (
                                    <div className="flex-1 mr-4">
                                        <input
                                            type="text"
                                            value={editingType?.typeName || ''}
                                            onChange={(e) => {
                                                if (editingType) {
                                                    setEditingType({
                                                        ...editingType,
                                                        typeName: e.target.value
                                                    });
                                                }
                                            }}
                                            className="w-full px-2 py-1 border rounded mb-2"
                                            placeholder="Type Name"
                                        />
                                        <input
                                            type="text"
                                            value={editingType?.description || ''}
                                            onChange={(e) => {
                                                if (editingType) {
                                                    setEditingType({
                                                        ...editingType,
                                                        description: e.target.value
                                                    });
                                                }
                                            }}
                                            className="w-full px-2 py-1 border rounded"
                                            placeholder="Description"
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        <h3 className="font-medium">{type.typeName}</h3>
                                        <p className="text-sm text-gray-500">{type.description}</p>
                                    </div>
                                )}
                                
                                <div className="flex space-x-2">
                                    {editingType?.locationTypeId === type.locationTypeId ? (
                                        <>
                                            <button
                                                onClick={() => {
                                                    if (editingType?.locationTypeId) {
                                                        handleUpdate(editingType);
                                                    }
                                                }}
                                                disabled={isSubmitting}
                                                className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={() => setEditingType(null)}
                                                className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button 
                                                onClick={() => setEditingType(type)}
                                                className="p-1 text-gray-400 hover:text-blue-600"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    if (type.locationTypeId) {
                                                        setShowDeleteConfirm(type.locationTypeId);
                                                    }
                                                }}
                                                className="p-1 text-gray-400 hover:text-red-600"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center mb-4">
                            <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
                            <h3 className="text-lg font-medium">Delete Location Type</h3>
                        </div>
                        
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to delete this location type? This may affect existing locations.
                        </p>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(showDeleteConfirm)}
                                disabled={isSubmitting}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            >
                                {isSubmitting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationTypeManager;